'use client';

import {
  BottomSheetHandleArea,
  BottomSheetTitle,
  BottomSheetTitleBox,
  BottomSheetTitleContainer,
  Dim,
  SheetContent,
  SheetDimMotion,
  SheetMotion,
  SheetPortal,
  Typography,
} from '@musinsa/mds';
import { useDeviceInfo } from '@musinsa/packages-device-detection';
import type { CurationSnapDetailGoodsItem } from '@musinsa/services-curator';
import clsx from 'clsx';
import type { FC } from 'react';
import { useNavigate } from '../../../common/hooks/useNavigate';
import { useProductLinkNavigation } from '../../../product/hooks';
import { SnapTaggedGoodsCard } from './SnapTaggedGoodsCard';

export interface SnapTaggedGoodsSheetContentProps {
  goodsItems: CurationSnapDetailGoodsItem[];
  invalidateSnapDetail: () => void;
  onOpenChange: (open: boolean) => void;
}

export const SnapTaggedGoodsSheetContent: FC<SnapTaggedGoodsSheetContentProps> = ({
  goodsItems,
  invalidateSnapDetail,
  onOpenChange,
}) => {
  const count = goodsItems.length;
  const { navigate } = useNavigate();
  const navigateToProduct = useProductLinkNavigation();
  const { isMobile } = useDeviceInfo();

  return (
    <SheetPortal>
      <SheetContent onPointerDownOutside={() => onOpenChange(false)}>
        <SheetMotion direction="bottom">
          <BottomSheetTitle>
            <BottomSheetHandleArea className="py-[6px]!" />
            <BottomSheetTitleContainer className="px-4 py-[8px]!">
              <BottomSheetTitleBox>
                <Typography variant="title16pxMed">{`태그 상품 ${count}개`}</Typography>
              </BottomSheetTitleBox>
            </BottomSheetTitleContainer>
          </BottomSheetTitle>
          <div className="max-h-[min(70dvh,560px)] divide-y divide-gray-100 overflow-y-auto bg-white safe-area-bottom">
            {goodsItems.map((good, index) => {
              const displayRate = good.finalDiscount ?? good.couponSalesRate ?? good.salesRate ?? 0;
              const displayPrice = good.finalPrice ?? good.couponPrice ?? good.price;

              const isFirstItem = index === 0;
              const isLastItem = index === goodsItems.length - 1;
              const isMiddle = !isFirstItem && !isLastItem;

              return (
                <SnapTaggedGoodsCard.Root
                  key={`sheet-${good.goodsNo}-${index}`}
                  size="m"
                  className={clsx(
                    isFirstItem && 'pt-[4px]! pb-[8px]!',
                    isMiddle && 'py-[8px]!',
                    isLastItem && 'pt-[8px]! pb-[12px]!',
                  )}
                  onClick={() => {
                    navigateToProduct(good.linkUrl);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigateToProduct(good.linkUrl);
                    }
                  }}
                >
                  <SnapTaggedGoodsCard.Image
                    goodsNo={good.goodsNo}
                    imageUrl={good.imageUrl}
                    initialIsLiked={good.isLiked}
                    invalidateSnapDetail={invalidateSnapDetail}
                    isMobile={isMobile}
                    isSoldOut={good.isSoldOut}
                    navigate={navigate}
                  />
                  <SnapTaggedGoodsCard.Info
                    brandName={good.brandName}
                    displayPrice={displayPrice}
                    displayRate={displayRate}
                    goodsName={good.goodsName}
                    isExclusive={good.isExclusive === true}
                  />
                </SnapTaggedGoodsCard.Root>
              );
            })}
          </div>
        </SheetMotion>
      </SheetContent>
      <SheetDimMotion>
        <Dim />
      </SheetDimMotion>
    </SheetPortal>
  );
};
